import React, {useMemo, useRef, useState} from "react";
import * as Yup from "yup";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import useIsMounted from "../../../hooks/use-is-mounted";
import {DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import {createUUId} from "../../../../core/services/utils/utils";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import {Col} from "reactstrap";
import Chips, {ChipsTypes} from "../../base/chips/chips";
import {Tab, Tabs} from "../../base/Tabs";
import UpsertFreightRegionDialogAddCountrySection from "./sections/add-country";
import UpsertFreightRegionDialogAddProvinceSection from "./sections/add-province";
import UpsertFreightRegionDialogAddPostalCodeSection from "./sections/add-postal-code";
import Api from "../../../../core/services/api/api";


const formNames = {
    title: 'title',
    countries: 'countries',
    provinces: 'provinces',
    postalCodes: 'postalCodes',
    error: 'error',
}

const schema = Yup.object().shape({
    [formNames.title]: Yup.string().trim().nullable().required(ValidateMessages.required),
    [formNames.countries]: Yup.array().nullable(),
    [formNames.provinces]: Yup.array().nullable(),
    [formNames.postalCodes]: Yup.array().nullable(),
    [formNames.error]: Yup.string().nullable(),
});

const validate = makeValidate(schema);
const required = makeRequired(schema);

const tabs = {
    addCountry: 'addCountry',
    addProvince: 'addProvince',
    addPostalCode: 'addPostalCode',
}

const tabsList = [
    {
        label: "Add Country",
        value: tabs.addCountry,
    },
    {
        label: "Add Province/State",
        value: tabs.addProvince,
    },
    {
        label: "Add Postal/Zip Code",
        value: tabs.addPostalCode,
    }
]


const UpsertFreightRegionDialog = ({open, close, onUpsert, data}) => {
    const [submitting, setSubmitting] = useState(false);
    const [tabValue, setTabValue] = useState(tabs.addCountry);
    const isMounted = useIsMounted();
    const formId = useRef(createUUId(true));
    /**@type {React.MutableRefObject<formApi>}*/
    const formRef = useRef();


    const initialValues = useMemo(() => ({
        [formNames.title]: data?.title ?? '',
        [formNames.countries]: data?.countries ?? [],
        [formNames.provinces]: data?.provinces ?? [],
        [formNames.postalCodes]: data?.postalCodes ?? [],
    }), [data?.countries, data?.postalCodes, data?.provinces, data?.title])

    /**
     * Submits the changes made in this dialog to either create a new region or update an existing one.
     *
     * * if the region does not have at least one country or province or Postal/Zip code, returns an error.
     * @param values
     * @return {Promise<{"[formNames.error]": string}>}
     */
    const onSubmit = async (values) => {
        const hasOneCountry = values[formNames.countries].length >= 1;
        const hasOneProvince = values[formNames.provinces].length >= 1;
        const hasOnePostalCode = values[formNames.postalCodes].length >= 1;
        if (!(hasOneCountry || hasOneProvince || hasOnePostalCode)) {
            return ({
                [formNames.error]: "At least one entry must be added to this region. " +
                    "Either a postal-code, a country or a province, or a combination of them"
            });
        }
        setSubmitting(true);
        if (!!data) {
            await editRegion(values);
        } else {
            await addRegion(values);
        }
    }

    /**
     * Edits the existing region (signified with data property) with the provided values arg.
     *
     * * if the result is successful, then closes the dialog and calls the [onUpsert] callback.
     * @param values
     * @return {Promise<void>}
     */
    const editRegion = async (values) => {
        const forApi = {
            id: data.id,
            title: values[formNames.title],
            countryIds: values[formNames.countries].map(e => e.id),
            provinceIds: values[formNames.provinces].map(e => e.id),
            postalCodes: values[formNames.postalCodes],
            updatedProperties: [
                "Title",
                "CountryIds",
                "ProvinceIds",
                "PostalCodes",
            ]
        }
        const response = await Api.updateSystemRegion(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            onUpsert(response.data);
            close();
        }
        setSubmitting(false);
    }

    /**
     * Add a new region with the provided values arg.
     *
     * * if the result is successful, then closes the dialog and calls the [onUpsert] callback.
     * @param values
     * @return {Promise<void>}
     */
    const addRegion = async (values) => {
        const forApi = {
            title: values[formNames.title],
            countryIds: values[formNames.countries].map(e => e.id),
            provinceIds: values[formNames.provinces].map(e => e.id),
            postalCodes: values[formNames.postalCodes],
        }
        const response = await Api.createSystemRegions(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            onUpsert(response.data);
            close();
        }
        setSubmitting(false);
    }

    /**
     * sets the current value of the tab.
     * @param e
     * @param newValue
     */
    const onTabChanged = (e, newValue) => {
        setTabValue(newValue);
    }


    const addCountry = (country) => {
        formRef.current?.change(formNames.countries, [...formRef.current.getState().values[formNames.countries], country])
        formRef.current?.resetFieldState(formNames.error);
    };
    const addProvince = (province) => {
        formRef.current?.change(formNames.provinces, [...formRef.current.getState().values[formNames.provinces], province])
        formRef.current?.resetFieldState(formNames.error);
    };
    const addPostalCode = (postalCode) => {
        formRef.current?.change(formNames.postalCodes, [...formRef.current.getState().values[formNames.postalCodes], postalCode])
        formRef.current?.resetFieldState(formNames.error);
    };

    return (
        <>
            <Dialog
                open={open}
                setOpen={close}
                maxWidth={'md'}
                className={'bound-dialog upsert-freight-region-dialog'}
            >
                <DialogTitle className={'p-0'} id="scroll-dialog-title">
                    <p className={'text-lg text-color-1 p-4 font-weight-semi-bold text-center'}>
                        {
                            !!data ? "Edit System Region" : "Create System Region"
                        }
                    </p>
                </DialogTitle>
                <DialogContent dividers className={'py-3'}>
                    <Form
                        className={'d-flex flex-wrap w-100'}
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={initialValues}
                        id={formId.current}
                        render={({values, form}) => {
                            formRef.current = form;
                            return (
                                <>
                                    <Col xs={12} className={'mb-3'}>
                                        <OutlinedInputForm
                                            label={'Title'}
                                            name={formNames.title}
                                            type={'text'}
                                            required={required[formNames.title]}
                                            value={values?.[formNames.title] ?? ''}
                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={12} className={'mb-4'}>
                                        <div className={'entities-container'}>
                                            <p className={'entity-title'}>
                                                Added Countries:
                                            </p>
                                            <div className={'d-flex flex-wrap'}>
                                                {values[formNames.countries].map(country => (
                                                    <Chips
                                                        key={country.id}
                                                        className={'mr-2 mb-2'}
                                                        type={ChipsTypes.remove}
                                                        content={country.name}
                                                        onIconClick={() => form.change(formNames.countries, values[formNames.countries].filter(e => e.id !== country.id))}
                                                    />
                                                ))}
                                            </div>
                                            <p className={'entity-title'}>
                                                Added Provinces:
                                            </p>
                                            <div className={'d-flex flex-wrap'}>
                                                {values[formNames.provinces].map(province => (
                                                    <Chips
                                                        key={province.id}
                                                        className={'mr-2 mb-2'}
                                                        type={ChipsTypes.remove}
                                                        content={province.name}
                                                        onIconClick={() => form.change(formNames.provinces, values[formNames.provinces].filter(e => e.id !== province.id))}
                                                    />
                                                ))}
                                            </div>
                                            <p className={'entity-title'}>
                                                Added Postal/Zip Codes:
                                            </p>
                                            <div className={'d-flex flex-wrap'}>
                                                {values[formNames.postalCodes].map(postalCode => (
                                                    <Chips
                                                        key={postalCode}
                                                        className={'mr-2 mb-2'}
                                                        type={ChipsTypes.remove}
                                                        content={postalCode}
                                                        onIconClick={() => form.change(formNames.postalCodes, values[formNames.postalCodes].filter(e => e !== postalCode))}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                        <OutlinedInputForm
                                            name={formNames.error}
                                            value={''}
                                            className={'hidden-input'}
                                        />
                                    </Col>
                                    <div className='w-100 flex-grow-1'>
                                        <Tabs value={tabValue ?? false} onChange={onTabChanged}>
                                            {
                                                tabsList.map(e => (
                                                    <Tab key={e.value} value={e.value} label={e.label}/>
                                                ))
                                            }
                                        </Tabs>
                                    </div>
                                    <div className={'w-100 px-3 py-4'}>
                                        {
                                            tabValue === tabs.addCountry &&
                                            <UpsertFreightRegionDialogAddCountrySection
                                                addCountry={addCountry}
                                                selectedCountries={values[formNames.countries]}
                                            />
                                        }
                                        {
                                            tabValue === tabs.addProvince &&
                                            <UpsertFreightRegionDialogAddProvinceSection
                                                addProvince={addProvince}
                                                selectedProvinces={values[formNames.provinces]}
                                            />
                                        }
                                        {
                                            tabValue === tabs.addPostalCode &&
                                            <UpsertFreightRegionDialogAddPostalCodeSection
                                                addPostalCode={addPostalCode}
                                                selectedPostalCodes={values[formNames.postalCodes]}
                                            />
                                        }
                                    </div>
                                </>
                            );
                        }}
                    />
                </DialogContent>
                <DialogActions className={'p-0 w-100'}>
                    <div className={'d-flex justify-content-end w-100'}>
                        <Button
                            disabled={!open || submitting}
                            variant={'dialog'}
                            color={'primary'}
                            form={formId.current}
                            type={'submit'}
                            borderRadius={'8px 0 8px 0'}
                        >
                            {
                                !!data
                                    ? submitting ? "Updating..." : "Update"
                                    : submitting ? "Creating..." : "Create"
                            }
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default UpsertFreightRegionDialog;
